import React, { useState, useEffect, useRef } from 'react';
import { Button, message } from 'antd';
import { GoogleOutlined } from '@ant-design/icons';
import authService from '../services/authService';

const GoogleOAuthButton = ({ onSuccess, onError, style = {} }) => {
  const [loading, setLoading] = useState(false);
  const [googleLoaded, setGoogleLoaded] = useState(false);
  const buttonRef = useRef(null);

  useEffect(() => {
    loadGoogleScript();
  }, []);

  useEffect(() => {
    if (googleLoaded && window.google) {
      initializeGoogleSignIn();
    }
  }, [googleLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadGoogleScript = () => {
    if (window.google) {
      setGoogleLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => setGoogleLoaded(true);
    script.onerror = () => {
      console.error('Failed to load Google script');
      message.error('Failed to load Google authentication');
    };
    document.head.appendChild(script);
  };

  const initializeGoogleSignIn = async () => {
    try {
      // Get Google OAuth client ID from backend
      const { client_id } = await authService.getGoogleAuthUrl();
      
      if (!client_id) {
        throw new Error('Google OAuth client ID not configured');
      }

      // Initialize Google Identity Services
      window.google.accounts.id.initialize({
        client_id: client_id,
        callback: handleCredentialResponse,
        auto_select: false,
        cancel_on_tap_outside: true,
      });

    } catch (error) {
      console.error('Google initialization error:', error);
    }
  };

  const handleCredentialResponse = async (response) => {
    setLoading(true);
    
    try {
      if (response.credential) {
        // Send JWT credential to backend for verification
        const result = await authService.googleLoginWithCredential(response.credential);
        
        message.success('Successfully logged in with Google!');
        
        if (onSuccess) {
          onSuccess(result);
        }
      } else {
        throw new Error('No credential received from Google');
      }
    } catch (error) {
      console.error('Google login error:', error);
      message.error(error.error || 'Google login failed');
      
      if (onError) {
        onError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    if (!googleLoaded || !window.google) {
      message.error('Google authentication not ready');
      return;
    }

    setLoading(true);
    
    try {
      // Trigger Google One Tap sign-in
      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          // If One Tap is not displayed, fall back to popup
          window.google.accounts.id.renderButton(buttonRef.current, {
            theme: 'outline',
            size: 'large',
            width: '100%',
            text: 'signin_with',
            shape: 'rectangular',
          });
          
          // Trigger click on the rendered button
          setTimeout(() => {
            const googleButton = buttonRef.current?.querySelector('[role="button"]');
            if (googleButton) {
              googleButton.click();
            }
            setLoading(false);
          }, 100);
        } else {
          setLoading(false);
        }
      });
    } catch (error) {
      console.error('Google login error:', error);
      message.error('Failed to initialize Google login');
      setLoading(false);
      
      if (onError) {
        onError(error);
      }
    }
  };

  return (
    <>
      <Button
        type="default"
        icon={<GoogleOutlined />}
        loading={loading}
        onClick={handleGoogleLogin}
        style={{
          width: '100%',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderColor: '#db4437',
          color: '#db4437',
          ...style
        }}
      >
        {loading ? 'Signing in...' : 'Sign in with Google'}
      </Button>
      {/* Hidden div for Google button rendering */}
      <div ref={buttonRef} style={{ display: 'none' }}></div>
    </>
  );
};

export default GoogleOAuthButton;
